import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import StandardPagination from './StandardPagination';
import useImpersonation from '../hooks/useImpersonation';
import ImpersonationModal from './ImpersonationModal';
import styles from './AllOrgs.module.css';

const AllOrgs = ({ organisations, searchQuery, setSearchQuery, handleSearch, handlePageChange, currentPage, totalPages, itemsPerPage, onItemsPerPageChange, totalItems }) => {
  const navigate = useNavigate();
  const { startImpersonation, loading } = useImpersonation();
  const [impersonationModal, setImpersonationModal] = useState({ show: false, user: null });

  const handleImpersonateClick = (org) => {
    setImpersonationModal({ show: true, user: org });
  };

  const handleImpersonateConfirm = async () => {
    const { user } = impersonationModal;
    if (!user) return;

    const result = await startImpersonation(user._id, 'organization');
    if (!result.success) {
      alert(result.error || 'Failed to start impersonation');
      setImpersonationModal({ show: false, user: null });
    }
  };

  const handleImpersonateCancel = () => {
    setImpersonationModal({ show: false, user: null });
  };

  return (
      <div className={styles.orgsTabContent}>
        <div className="container">
          <div>
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
              <Form onSubmit={handleSearch} className="form-inline">
                <Form.Control
                  className="form-control mr-sm-2"
                  type="search"
                  name="search"
                  placeholder="Organisation Name PostCode"
                  aria-label="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button className="btn btn-outline-success my-2 my-sm-0" type="submit">
                  Search
                </Button>
              </Form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <label htmlFor="orgsPerPage" className="form-label me-2 mb-0">Organizations per page:</label>
                  <select 
                    id="orgsPerPage"
                    className="form-select"
                    value={itemsPerPage || 12}
                    onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value))}
                    style={{ 
                      maxWidth: '80px', 
                      height: '32px',
                      fontSize: '14px',
                      padding: '4px 8px'
                    }}
                  >
                    {[10, 20, 50, 100].map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6 text-end">
                <small style={{ color: 'var(--darkOrange)' }}>
                  Showing {((currentPage - 1) * (itemsPerPage || 12)) + 1} to {Math.min(currentPage * (itemsPerPage || 12), totalItems || organisations.length)} of {totalItems || organisations.length} organizations
                </small>
              </div>
            </div>
          <div className={styles.cardGrid}>
            {organisations.map(org => (
              <div key={org._id} className={styles.cardItem}>
                <div className={styles.card}>
                  <div className={styles.cardBody}>
                    <div className={styles.cardContent}>
                      <div className={styles.cardInfo}>
                        <h5 className={styles.cardTitle}>{org.name}</h5>
                        <p className={styles.cardSubtitle} title={org.code}>Code: {org.code}</p>
                        <p className={styles.cardSubtitle} title={org.contactName}>Contact: {org.contactName}</p>
                        <p className={styles.cardSubtitle} title={org.email}>Email: {org.email}</p>
                      </div>
                      <div className={styles.cardButton}>
                        <Button 
                          className={`btn ${styles.btnPrimary} ${styles.btnLeft}`}
                          onClick={() => navigate(`/organisation/${org._id}`)}
                        >
                          View<br/>Details
                        </Button>
                        <Button 
                          className={`btn ${styles.btnWarning} ${styles.btnRight}`}
                          onClick={() => handleImpersonateClick(org)}
                          title="Impersonate User"
                          disabled={loading}
                        >
                          Impersonate<br/>User
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <StandardPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage || 12}
            onItemsPerPageChange={onItemsPerPageChange}
            totalItems={totalItems || organisations.length}
            itemsPerPageLabel="Organizations per page:"
            pageInfoLabel="Showing"
            showItemsPerPage={false}
          />
          <br />
        </div>
      </div>

      <ImpersonationModal
        show={impersonationModal.show}
        onClose={handleImpersonateCancel}
        onConfirm={handleImpersonateConfirm}
        user={impersonationModal.user}
        loading={loading}
      />
    </div>
  );
};

export default AllOrgs; 